import React, { useState } from 'react';
import { ChevronDownIcon, ClipboardListIcon, GiftIcon, MapIcon, MessageSquareTextIcon, NotebookPenIcon, TimerIcon } from 'lucide-react';
import { CheckList } from './CheckList';
import { Medallion } from './Medallion';
import { Bonus } from '../data/bonuses';
interface BonusCardProps {
  bonus: Bonus;
}
/** Each command kit keeps the symbol printed on its cover. */
const icons: Record<Bonus['icon'], typeof GiftIcon> = {
  builder: TimerIcon,
  playbook: MapIcon,
  meet: ClipboardListIcon,
  tracker: NotebookPenIcon,
  scripts: MessageSquareTextIcon
};

/** Compact row: kit, value and promise; full contents one tap away. */
export function BonusCard({
  bonus
}: BonusCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = `bonus-${bonus.number}-details`;
  return <li className="rounded-2xl border border-line bg-white shadow-sm" data-id="element-25" data-mp="1hormnno6hzcx">
      <div className="flex gap-3.5 p-4" data-id="element-26" data-mp="1lpjtybdhpu6k">
        <div className="relative shrink-0 self-start" data-id="element-27" data-mp="1lo8takfcd2jo">
          <img src={bonus.cover} alt={`Cover of ${bonus.title}`} className="h-24 w-[72px] rounded-md object-cover shadow-md shadow-navy/20 ring-1 ring-navy/10 sm:h-28 sm:w-[84px]" width={896} height={1200} sizes="84px" loading="lazy" decoding="async" data-id="element-28" data-mp="1x24v2ce5vg02" />
          <span className="absolute -bottom-2 -right-2" data-id="element-29" data-mp="1naqr9sf6fu14">
            <Medallion icon={icons[bonus.icon]} className="!h-7 !w-7 border-2 border-white [&>svg]:!h-3.5 [&>svg]:!w-3.5" data-id="element-30" data-mp="1idql7hibura0" />
          </span>
        </div>
        <div className="min-w-0 flex-1" data-id="element-31" data-mp="1dfgqt94ffupg">
          <div className="flex items-center justify-between gap-2" data-id="element-32" data-mp="19csid2jmkpt0">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gold-text" data-id="element-33" data-mp="19emze5rrzxrt">
              Command Kit {bonus.number.toString().padStart(2, '0')}
            </p>
            <p className="shrink-0 text-xs" data-id="element-34" data-mp="zfxk8kr9krf9">
              <span className="text-signal line-through" data-id="element-35" data-mp="2yr8er30sqmbm">{bonus.value}</span>{' '}
              <span className="font-bold text-success" data-id="element-36" data-mp="2yp4b8m6t7pbv">FREE</span>
            </p>
          </div>
          <h3 className="mt-1 font-display text-lg font-bold leading-snug text-navy" data-id="element-37" data-mp="2tqkpomw4ofk1">
            {bonus.title}
          </h3>
          <p className="mt-1 text-xs leading-relaxed text-body sm:text-sm" data-id="element-38" data-mp="wugbqb1drp6j">
            {bonus.subtitle}
          </p>
        </div>
      </div>

      <button type="button" onClick={() => setIsOpen(value => !value)} aria-expanded={isOpen} aria-controls={panelId} className="flex w-full items-center justify-between gap-2 border-t border-line px-4 py-2.5 text-sm font-semibold text-gold-text focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-gold-text" data-id="element-39" data-mp="i6lk6xz3zqv1">
        {isOpen ? 'Hide details' : 'See what’s inside'}
        <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ease-out ${isOpen ? 'rotate-180' : ''}`} strokeWidth={2.25} aria-hidden="true" data-id="element-40" data-mp="10xmf9ax3z29b" />
      </button>

      <div id={panelId} className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-41" data-mp="3lkzf9jncott6">
        <div className="overflow-hidden" data-id="element-42" data-mp="28mfmyn5rxpwh">
          <div className="space-y-4 border-t border-line px-4 py-4" data-id="element-43" data-mp="28pblacljaglg">
            <CheckList items={bonus.items} data-id="element-44" data-mp="23ncfyx68h9lv" />
            {bonus.why ? <p className="rounded-lg bg-canvas p-3 text-sm italic leading-relaxed text-navy" data-id="element-45" data-mp="2hnn966v0n0a7">
                {bonus.why}
              </p> : null}
          </div>
        </div>
      </div>
    </li>;
}